import React from 'react'
import { PropsWithChildren } from 'react'
import { AuthResponse } from '../preContest/store/types';
import { APIState } from '../utils/api/reducer';
import { setAuth, setSite, setSlate, setSport } from './store/actions';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { connect } from 'react-redux';
import { SitesResponse, SlateResponse, SportsResponse } from './store/types';
import { useGetSlates } from './queries/slateQueries';
import { filterSlates, getSupportedSports, SupportedSites } from './helpers/SelectorHelpers';
import { encodeEmail, setPersistentUserId } from '../../utils/persistentUserId';
import { APIConstants } from '../utils/api/constants';
import { setUserCookie } from '../../utils/userCookie';

interface OwnProps {
  readonly app: string;    
}

interface ReduxProps {
  readonly authenticated: APIState<AuthResponse>;
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedSlate: SlateResponse;
  readonly selectedType: string;
}

interface DispatchProps {
  SetAuth: (league: string, email: string, appName: string) => void;
  SetSport: (sport: SportsResponse) => void;
  SetSite: (site: SitesResponse) => void;
  SetSlate: (slate: SlateResponse) => void;
}


type Props = OwnProps & ReduxProps & DispatchProps;

const Authentication = (props: PropsWithChildren<Props>) => {
  const { app, authenticated, selectedSport, selectedSite, selectedType, selectedSlate, SetAuth, SetSport, SetSite, SetSlate } = props;
  const { user, isAuthenticated } = useAuth0();
  const userEmail = isAuthenticated ? user?.email || '' : '';
  const routeParams = useParams<{ league: string }>();
  const {data, isLoading: slatesLoading} = useGetSlates(app, false);
  const slates = data?.slates;
  const sports = getSupportedSports(app);
  const sites = SupportedSites;

  React.useEffect(() => {
    if (slates && slates?.length > 0) {
      const filteredSlates = filterSlates(slates, selectedSport, selectedSite, selectedType);
      const slate = filteredSlates.find(x => x.slateId === selectedSlate.slateId);
      SetSlate(slate || filteredSlates[0] || {} as SlateResponse);
    }
    const loading = slatesLoading;
    if (!loading && slates && slates?.length === 0) {
      SetSlate({} as SlateResponse);
    }
  }, [slates])

  React.useEffect(() => {
    // Always sync sport with URL parameter
    const sport = sports.find(x => x.sport?.toLowerCase() === routeParams.league?.toLowerCase());
    if (sport && sport.sport !== selectedSport?.sport) {
      SetSport(sport || sports[0]);
    } else if (!selectedSport || !selectedSport.sport) {
      SetSport(sports[0]);
    }

    if (!selectedSite || !selectedSite.site) {
      SetSite(sites[0]);
    }
  }, [routeParams.league, sports, selectedSport, selectedSite, SetSport, SetSite])
  
  React.useEffect(() => {
    if (isAuthenticated && userEmail) {
      SetAuth(
        routeParams.league,
        userEmail,
        ['SINGLELINEUPSIM', 'PICKEM', 'DATAHUB'].includes(app) ? app : 'CONTESTSIM',
      );
    }
  }, [selectedSport, isAuthenticated, userEmail])

  React.useEffect(() => {
    if (authenticated?.status === APIConstants.SUCCEEDED && authenticated?.data && userEmail) {
      const email = userEmail.toLowerCase().trim();
      setPersistentUserId(userEmail);
      setUserCookie({
        ...authenticated.data,
        id: encodeEmail(userEmail),
        email,
        username: email,
      });
    }
  }, [authenticated?.status, authenticated?.data, userEmail])

  return (
    <>
      {props.children}
    </>
  );
}

const mapStateToProps = (state: any) => {
  const {
    authenticated,
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate,
  } = state.common;
  return {
    authenticated,
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate
  };
}

const mapDispatchToProps = {
  SetAuth: setAuth,
  SetSport: setSport,
  SetSite: setSite,
  SetSlate: setSlate,
}

export default connect(mapStateToProps, mapDispatchToProps)(Authentication)
